import { useEffect, useState } from "react";
import tradeFromScratchStore, {
  DEFAULT_TICKET_INSTRUMENT,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";
import {
  SENT_BUTTON_CONFIRMATION_MS,
  returnSentConfirmationRemainingMs,
} from "./ticketSharedHelper";

// The send/confirm state every Leg Creation ticket shares: instrument, busy flag, the
// blocked-Send hover and both confirmation timers. Field rendering lives elsewhere, so
// Trade Creation draft legs can reuse the fields without this shell.
export function useTicketShell(ticketId: string) {
  const instrument = tradeFromScratchStore(
    (state) => state.instrumentByTicketId[ticketId] ?? DEFAULT_TICKET_INSTRUMENT,
  );
  const isSending = tradeFromScratchStore(
    (state) => state.isSendingByTicketId[ticketId] ?? false,
  );
  const lastSent = tradeFromScratchStore(
    (state) => state.lastSentByTicketId[ticketId],
  );
  const setTicketInstrument = tradeFromScratchStore(
    (state) => state.setTicketInstrument,
  );
  const clearLastSent = tradeFromScratchStore(
    (state) => state.clearLastSentTicket,
  );

  // Held on the wrapper, not the button - a disabled button fires no mouse events.
  const [isHoveringSend, setIsHoveringSend] = useState(false);

  // Local, not from the store, so a remount never re-flashes it; the timestamp is the
  // state so a second send restarts the timer.
  const [sentAtOnButton, setSentAtOnButton] = useState(0);

  useEffect(() => {
    if (!sentAtOnButton) return;
    const timer = setTimeout(
      () => setSentAtOnButton(0),
      SENT_BUTTON_CONFIRMATION_MS,
    );
    return () => clearTimeout(timer);
  }, [sentAtOnButton]);

  // A second send replaces the entry, so the first countdown never keeps running.
  useEffect(() => {
    if (!lastSent) return;
    const timer = setTimeout(
      () => clearLastSent(ticketId),
      returnSentConfirmationRemainingMs(lastSent.sentAt, Date.now()),
    );
    return () => clearTimeout(timer);
  }, [lastSent, ticketId, clearLastSent]);

  return {
    instrument,
    setInstrument: (next: TicketInstrument) =>
      setTicketInstrument(ticketId, next),
    isSending,
    lastSent,
    isHoveringSend,
    setIsHoveringSend,
    isConfirmingSend: sentAtOnButton > 0,
    markSent: () => setSentAtOnButton(Date.now()),
  };
}

const NO_KEYS = new Set<string>();

// The rows to flag red: only while the user hovers a blocked Send.
export function returnHighlightedKeys(
  isHoveringSend: boolean,
  canSend: boolean,
  missingEntries: { key: string }[],
): Set<string> {
  if (!isHoveringSend || canSend) return NO_KEYS;
  return new Set(missingEntries.map((entry) => entry.key));
}
